import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import { useResetOnKeyChange } from "@/hooks/use-reset-on-key-change";
import { ItemActionArea } from "@/components/ui/item-action-area";
import { ItemEmptyImageBox } from "@/components/ui/item-image-box";
import { ITEM_GALLERY_BORDER } from "@/components/ui/item-image-box-sizes";
import { ItemPicture } from "@/components/ui/item-picture";
import { ItemThumbnailStrip } from "@/components/ui/item-thumbnail-strip";
import {
  ITEM_PLACEHOLDER_ALT,
  itemImageSources,
  type CatalogItemImageInput,
} from "@/lib/catalog-item-image";
import { cn } from "@/lib/utils";

// The donor's shared gallery — `timmy/src/components/assets/ImagePreview.tsx`:
// one large main image over a wrapping strip of square thumbnails, with a click
// on a thumbnail swapping it into the main slot.
//
// **Here rather than in the catalog folder because it is shared in the source**
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"). It lives in `timmy/src/components/assets/`, the
// same shared asset tree `ItemEmptyImageBox` came from and which
// `item-image-box.tsx` beside this file already ports. The *choice* between this
// gallery and the empty box is the details page's, and that lives in
// `components/catalog/item-gallery.tsx` — the donor draws the same line
// (`Lightbox/Images.tsx` is feature-local to `ItemDetailsIsland`).
//
// Three things the donor does that this deliberately does **not**:
//
//   - **`isSelected` and its "Selected" chip.** Hard-coded `false` by the details
//     adapter (`Lightbox/Images.tsx:42`), which is what selects the #E8E8E8
//     border over the `#000` one and suppresses `SelectedCardText`
//     (`ImagePreview.tsx:42,52`). Live code in the donor, dead on this surface —
//     so it is not ported. Resurrecting it would put a selection affordance on a
//     screen that has no selection.
//   - **`?w=256` on the opening image only.** `utils.ts:19` sizes the derived
//     hero and nothing else: the thumbnails render raw URLs (`:62`) and the swap
//     promotes `event.target.src` (`:23`), so the donor's main slot silently
//     upgrades to full-resolution original bytes after the first click. Here one
//     image is one `<picture>` and the browser picks the rung against the slot's
//     own `sizes` — the approved ADR-0003 divergence — so the distinction has
//     nowhere to land.
//   - **Its dead click handlers.** Clicking the donor's main image is a no-op
//     that re-sets its own `src` (`ImagePreview.tsx:44`), and keyboard activation
//     of the wrapping `CardActionArea` does nothing because the handler sits on
//     the inner `<img>` (`:29,44`). The lightbox is what that affordance was
//     always shaped like, ruled by spec #269 (stories 7-8), so here the main slot
//     is a real button that opens it — and the keyboard path works.

interface ItemImagePreviewProps {
  /**
   * The item's images, hero first.
   *
   * Never empty: an item with no images is the *other* branch entirely, and the
   * caller picks between them (`components/catalog/item-gallery.tsx`, the donor's
   * `Lightbox/Images.tsx:26-29`). A gallery handed nothing would have to invent
   * the placeholder's geometry, which is `ItemEmptyImageBox`'s to own.
   */
  images: readonly CatalogItemImageInput[];
  /**
   * The item's name, and the main image's `alt` verbatim.
   *
   * `ImagePreview.tsx:35` reads `asset?.name`, which `Images.tsx:32` fills from
   * `item.name || ''` — so this is the **bare name**, not the card's `Image of
   * {name}`. `itemImageAlt` is that other donor arm and using it here would
   * describe the picture differently than modsy does. A nameless item yields
   * `alt=""`, which marks the picture decorative; that is the donor's own answer
   * and not something to improve on.
   *
   * The donor's `CardActionArea` carries no label of its own and so takes this
   * as its accessible name too, announcing nothing about what activating it
   * does. Atlas names it instead — "View larger image of {name}" — an
   * **owner-ruled parity break** (#303, D2), on the grounds that this button
   * genuinely opens something modsy's does not.
   */
  itemName: string | null | undefined;
  /**
   * Open one image at lightbox size — spec #269, stories 7-8.
   *
   * **Optional, and its absence is a surface the donor already has.** Modsy has
   * no lightbox anywhere: `ImagePreview.tsx:44`'s `onClick` re-sets the image's
   * own `src` and keyboard activation of the wrapping `CardActionArea` reaches
   * nothing at all. Atlas added the dialog to the authed details page, and the
   * anonymous product page keeps the donor's behaviour (#270 lists the swap as
   * "a static swap, not a carousel or lightbox") — so it passes no handler and
   * gets the dead affordance, hover and ripple included.
   *
   * The picture keeps `cursor: pointer` either way, because the donor sets it
   * unconditionally (`ImagePreview.tsx:37`) on a slot whose click does nothing.
   */
  onOpenLightbox?: (index: number) => void;
  /** Whether the item is approved — the placeholder's pending variant. */
  isApproved?: boolean;
}

export function ItemImagePreview({
  images,
  itemName,
  onOpenLightbox,
  isApproved = true,
}: ItemImagePreviewProps) {
  // Which image is in the main slot.
  //
  // The donor resets this in an effect keyed on the derived hero *string*
  // (`ImagePreview.tsx:18-20`), which has a visible consequence the sweep found:
  // a variant whose hero URL matches the previous one does not reset the user's
  // thumbnail choice. Here a variant switch is a route navigation, so the honest
  // key is the image set itself — see `useResetOnKeyChange` for why that is the
  // key and why the reset happens during render.
  const setKey = images.map((image) => image.id).join(",");
  const [selected, setSelected] = useResetOnKeyChange(setKey, 0);
  // A set that shrank under a held selection — a removed image, an image whose
  // fetch failed, a shorter variant — must not index past its end.
  const index = selected >= images.length ? 0 : selected;

  const main = images[index];
  const mainSources = itemImageSources(main);
  // This slot's own verdict, not the image's — the thumbnail of the very same
  // picture is a different fetch at a different rung (`useItemImageFailure`).
  const [mainFailed, noteMainFailed] = useItemImageFailure(mainSources);
  // `ImagePreview.tsx:55` — the strip is for items that have something to
  // choose between.
  const showStrip = images.length > 1;

  return (
    <div data-testid="item-image-preview">
      {/* `ImagePreview.tsx:28-53` — the `mb: 3` Box and the `CardActionArea`
        inside it. The 24px is **here, on the main slot**, and not around the
        whole gallery: the donor's Box wraps the action area alone and the strip
        at `:55` is its sibling, so the 24px is the gap *between* the picture and
        the thumbnails. Carrying it on the outside instead leaves the picture
        flush against the strip and strands the space below everything.

        A real button, where #251 could only draw the hover highlight, because
        now there is something to activate. */}
      <ItemActionArea
        data-testid="gallery-main-image"
        // The name modsy never says, ruled in on #303 (D2): the donor computes
        // this control's name from the picture's `alt`, which is the bare item
        // name and silent about the lightbox behind it.
        // ...and only while there *is* a lightbox. The break was granted on the
        // grounds that this button opens something modsy's does not; with no
        // handler behind it those grounds are gone, and the donor's own naming
        // applies — the action area carries no label and takes the picture's
        // `alt`, which is the bare item name.
        ariaLabel={
          onOpenLightbox === undefined
            ? undefined
            : itemName?.trim()
              ? `View larger image of ${itemName.trim()}`
              : "View larger image"
        }
        className="mb-6"
        // **Clickable even when this slot's picture died.** The placeholder
        // stands in for the *slot*, not for the image: this slot resolved the
        // ladder against half the page, where the lightbox resolves it against
        // its own 1120px paper (#499), so the rung that 404'd here says nothing
        // about the rung the dialog will ask for — and #303 AC 1 has every image
        // opening in the lightbox. The dialog draws its own fallback if its own
        // fetch dies too.
        //
        // The *no-image* arm is the one that activates nothing, and it is the
        // donor's own dead affordance (`ImagePreview.tsx:29,44` — `ItemGallery`).
        onClick={onOpenLightbox && (() => onOpenLightbox(index))}
      >
        {mainFailed ? (
          // `#303` AC 2 — the same placeholder the no-image arm draws, in the
          // slot whose fetch failed. The donor has no `onError`, because its
          // image host serves a placeholder PNG for a missing object and its
          // `<img>` therefore loads (#496); Atlas has to draw one.
          <ItemEmptyImageBox
            isPendingApproval={!isApproved}
            alt={ITEM_PLACEHOLDER_ALT}
            heightPx={350}
            bordered="light"
          />
        ) : (
          /* **Not `ItemImageBox`**, and the reason is the donor's own shape:
            `CardMedia component='img'` renders the `<img>` *itself* with the
            border, the radius and the cap (`ImagePreview.tsx:33-43`;
            `CardMedia.js:44-62`). It is a picture that sizes itself, not a fixed
            box with a picture contained inside it — which is exactly what
            `ItemImageBox` is, and what the thumbnails below genuinely are.
            Forcing this slot through that component would put the border on a
            wrapper and leave the `<img>` to fill it, so the hairline would no
            longer trace the picture's own edge. */
          <ItemPicture
            sources={mainSources}
            // The donor's own alt, verbatim — and the action area's accessible
            // name with it. See `itemName` above.
            alt={itemName?.trim() ?? ""}
            // The gallery column, to the pixel — and the gutters are not
            // rounding error at this size. `item-detail-page.tsx` spends 112px
            // horizontally (the page's `px-10` outside the island's `px-4`) and
            // the two-column grid another 32px on its `gap-8`, so the column is
            // `50vw - 72px`, not `50vw`. Claiming the round number overstates
            // the slot by ~72px, which at a rung boundary is the difference
            // between fetching w512 and fetching w1024 for a picture capped at
            // 350px.
            //
            // The 900px is MUI's `md`, which is what the page breaks at.
            sizes="(min-width: 900px) calc(50vw - 72px), calc(100vw - 112px)"
            // `ImagePreview.tsx:33,36-41` — full column width, height auto,
            // capped at 350px. `object-contain` only bites once the cap does,
            // where the box is columnWidth x 350 and a portrait letterboxes
            // horizontally. `block` is `CardMedia`'s own (`CardMedia.js:47`),
            // and it is what keeps a baseline gap out from under the picture.
            className={cn(
              ITEM_GALLERY_BORDER,
              "block h-auto max-h-[350px] w-full cursor-pointer object-contain",
            )}
            // Above the fold, and the donor sets no hint at all
            // (`ImagePreview.tsx:31-45`); #302's lazy default is scoped by its
            // own reasoning to the card grids.
            loading="eager"
            onError={noteMainFailed}
          />
        )}
      </ItemActionArea>

      {showStrip && (
        <ItemThumbnailStrip
          data-testid="gallery-thumbnails"
          images={images}
          onSelect={setSelected}
          isApproved={isApproved}
        />
      )}
    </div>
  );
}
